import { useMemo, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { MetricToggle } from "./MetricToggle";
import { teamMonthly, quarterReview } from "../lib/summary";
import { toQuarters, fmtDur } from "../lib/pickerSheet";
import { fmtNum, slug } from "../lib/format";

const METRICS = [["vph", "Visits / hr"], ["qph", "Qty / hr"], ["lostDay", "Lost / day"]];
const fmtFor = (m) => (m === "lostDay" ? fmtDur : fmtNum);

const QSelect = ({ value, onChange, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId} className="h-9 w-[130px] bg-[#0B0F19] border-slate-700 text-slate-100 font-mono text-xs"><SelectValue /></SelectTrigger>
    <SelectContent>{options.map((q) => <SelectItem key={q.key} value={q.key} className="font-mono text-xs">{q.key}{q.months < 3 ? ` (${q.months} mo)` : ""}</SelectItem>)}</SelectContent>
  </Select>
);

const Mover = ({ title, rows, metric, tone, testId, onOpen }) => (
  <div className="panel p-5" data-testid={testId}>
    <p className="label-mono mb-3">{title}</p>
    {rows.length === 0 && <p className="text-xs text-slate-500">Not enough data.</p>}
    {rows.map((r, i) => (
      <button key={r.id} onClick={() => onOpen(r.id)} className="w-full flex items-center gap-3 py-1.5 text-left hover:bg-slate-800/40 rounded px-1">
        <span className="font-mono text-xs text-slate-500 w-4">{i + 1}</span>
        <span className="text-sm text-slate-100 flex-1 truncate">{r.id}</span>
        <span className="font-mono text-xs text-slate-400">{fmtFor(metric)(r.a)} → {fmtFor(metric)(r.b)}</span>
        <span className={`font-mono text-xs w-16 text-right ${tone}`}>{r.change >= 0 ? "▲" : "▼"} {Math.abs(r.change).toFixed(1)}%</span>
      </button>
    ))}
  </div>
);

export const QuarterReview = ({ raw, months, cfg, onOpen }) => {
  const { perUser, team } = useMemo(() => teamMonthly(raw, months, cfg), [raw, months, cfg]);
  const quarters = useMemo(() => toQuarters(team), [team]);
  const full = quarters.filter((q) => q.months === 3);
  const [qa, setQa] = useState((full[full.length - 2] || quarters[0])?.key);
  const [qb, setQb] = useState((full[full.length - 1] || quarters[quarters.length - 1])?.key);
  const [metric, setMetric] = useState("vph");
  const rows = useMemo(() => quarterReview(perUser, qa, qb, metric), [perUser, qa, qb, metric]);
  const max = Math.max(1, ...rows.map((r) => Math.abs(r.change)));
  return (
    <div className="space-y-6" data-testid="quarter-review">
      <div className="panel px-4 py-3 flex flex-wrap items-center gap-3">
        <span className="label-mono">Compare</span>
        <QSelect value={qa} onChange={setQa} options={quarters} testId="qr-quarter-a" />
        <span className="text-slate-500">→</span>
        <QSelect value={qb} onChange={setQb} options={quarters} testId="qr-quarter-b" />
        <MetricToggle value={metric} onChange={setMetric} options={METRICS} prefix="qr-metric" />
        <span className="text-[11px] text-slate-500 lg:ml-auto">Pickers need 20+ hours in both quarters to be ranked.</span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Mover title="Most improved" rows={rows.filter((r) => r.score > 0).slice(0, 5)} metric={metric} tone="text-emerald-400" testId="qr-improved" onOpen={onOpen} />
        <Mover title="Biggest decline" rows={rows.filter((r) => r.score < 0).reverse().slice(0, 5)} metric={metric} tone="text-rose-400" testId="qr-declined" onOpen={onOpen} />
      </div>
      <div className="panel overflow-x-auto" data-testid="qr-table">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-800">{["#", "Picker", qa, qb, "Change", ""].map((h, i) => <th key={i} className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-slate-500 first:pl-5">{h}</th>)}</tr></thead>
          <tbody>{rows.map((r, i) => (
            <tr key={r.id} data-testid={`qr-row-${slug(r.id)}`} onClick={() => onOpen(r.id)} className="border-b border-slate-800/60 hover:bg-slate-800/40 cursor-pointer">
              <td className="pl-5 py-2.5 font-mono text-xs text-slate-500">{i + 1}</td>
              <td className="px-4 text-slate-100 font-medium">{r.id}</td>
              <td className="px-4 font-mono text-slate-400">{fmtFor(metric)(r.a)}</td>
              <td className="px-4 font-mono text-slate-200">{fmtFor(metric)(r.b)}</td>
              <td className={`px-4 font-mono ${r.score >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{r.change >= 0 ? "▲" : "▼"} {Math.abs(r.change).toFixed(1)}%</td>
              <td className="px-4 w-56">
                <div className="relative h-2 rounded bg-slate-800">
                  <div className={`absolute top-0 h-2 rounded ${r.score >= 0 ? "bg-emerald-500 left-1/2" : "bg-rose-500 right-1/2"}`} style={{ width: `${(Math.abs(r.change) / max) * 50}%` }} />
                </div>
              </td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
};
